import { reactive } from 'vue'
import { isWechat } from '@/utils/wechatShare'

/**
 * 微信浮窗引导横幅
 *
 * 触发条件（全部满足才展示）：
 * 1. 当前为微信内置浏览器（UA 含 MicroMessenger）
 * 2. 本地未记录 has_shown_float_guide=true（用户勾选「不再提示」后永久不再弹）
 * 3. 满足触发时机之一：
 *    a. 用户刚完成关键动作（盲盒报名成功 / AI红娘预约确认成功），由业务页调用 tryShowFloatGuide
 *    b. 登录用户进入盲盒CP页面（方便找到回来的路，不错过沟通时机）
 *
 * 关闭语义：勾选「不再提示」后关闭 → 永久不再弹；仅点 × 关闭 → 本次关闭，后续满足时机仍会提示。
 * 非微信环境不弹；组件唯一挂载点：App.vue 的 <FloatGuideBanner />，业务页只调触发方法，不感知 UI。
 */

/** localStorage 记录 key：关闭引导时写入 'true'，全局只弹一次 */
export const STORAGE_KEY_FLOAT_GUIDE = 'has_shown_float_guide'
/** 已展示标记值 */
const FLOAT_GUIDE_DONE = 'true'
/** 关键动作成功后延迟展示（毫秒）：先让成功 toast 完整呈现，避免与横幅同时出现抢视觉 */
const FLOAT_GUIDE_SHOW_DELAY_MS = 1500

const state = reactive({
  visible: false
})

let showTimer: ReturnType<typeof setTimeout> | null = null

function isGuided(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_FLOAT_GUIDE) === FLOAT_GUIDE_DONE
  } catch {
    // localStorage 不可用（隐私模式等）时本次会话内仍允许弹一次
    return false
  }
}

/**
 * 关键动作成功后尝试弹出浮窗引导。
 * 不满足条件（非微信/已引导过/已在展示或等待中）时静默忽略，不影响业务流程。
 */
export function tryShowFloatGuide(delayMs: number = FLOAT_GUIDE_SHOW_DELAY_MS): void {
  if (state.visible || showTimer) return
  if (!isWechat()) return
  if (isGuided()) return

  showTimer = setTimeout(() => {
    showTimer = null
    // 等待期间若已被其他路径关闭/记录，则不再展示
    if (!state.visible && !isGuided()) state.visible = true
  }, delayMs)
}

/**
 * 关闭横幅。
 * @param forever true=用户勾选了「不再提示」，落本地记录永久不再弹；
 *                false=仅本次关闭，后续满足触发时机时仍可提示
 */
export function closeFloatGuide(forever = false): void {
  if (showTimer) {
    clearTimeout(showTimer)
    showTimer = null
  }
  state.visible = false
  if (!forever) return
  try {
    localStorage.setItem(STORAGE_KEY_FLOAT_GUIDE, FLOAT_GUIDE_DONE)
  } catch {
    // 写入失败（隐私模式等）不阻塞关闭动作
  }
}

export function useFloatGuide() {
  return { state, tryShowFloatGuide, closeFloatGuide }
}
